'use client';

import type { ScheduledReportFrequency } from '@terra/shared-contracts-applications';
import { VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { automationId } from '@terra/shared-utils';
import { Checkbox } from '../../../components/checkbox';
import { RadioGroup } from '../../../components/radio-group';
import { SettingsEditDialog } from '../SettingsEditDialog';

export type ScheduledReportFrequencyDialogProps = {
  open: boolean;
  disabled?: boolean;
  availableFrequencies: ScheduledReportFrequency[];
  selectionMode?: 'single' | 'multiple';
  initialFrequencies?: ScheduledReportFrequency[];
  submitText?: string;
  onOpenChange: (open: boolean) => void;
  onAdd: (frequencies: ScheduledReportFrequency[]) => void;
  contentAutomationId?: string;
  submitAutomationId?: string;
  frequencyOptionAutomationIds?: Partial<
    Record<ScheduledReportFrequency, string>
  >;
};

export const ScheduledReportFrequencyDialog = ({
  open,
  disabled,
  availableFrequencies,
  selectionMode = 'multiple',
  initialFrequencies,
  submitText,
  onOpenChange,
  onAdd,
  contentAutomationId,
  submitAutomationId,
  frequencyOptionAutomationIds,
}: ScheduledReportFrequencyDialogProps) => {
  const t = useTranslations('designSystem.automation.scheduledReports');
  const [selectedFrequencies, setSelectedFrequencies] = useState<
    ScheduledReportFrequency[]
  >([]);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      const allowed = new Set(availableFrequencies);
      const next = (initialFrequencies ?? []).filter((frequency) =>
        allowed.has(frequency),
      );

      setSelectedFrequencies(
        selectionMode === 'single' ? next.slice(0, 1) : next,
      );
    }

    if (!open) {
      setSelectedFrequencies([]);
    }

    wasOpenRef.current = open;
  }, [open, availableFrequencies, initialFrequencies, selectionMode]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setSelectedFrequencies((current) =>
      current.filter((frequency) => availableFrequencies.includes(frequency)),
    );
  }, [availableFrequencies, open]);

  const frequencyItems = useMemo(
    () =>
      availableFrequencies.map((frequency) => ({
        value: frequency,
        label: t(`frequencies.${frequency}`),
      })),
    [availableFrequencies, t],
  );

  const isSingle = selectionMode === 'single';
  const selectedFrequency = selectedFrequencies[0] ?? '';

  return (
    <SettingsEditDialog
      open={open}
      title={t('frequencyModalTitle')}
      submitText={submitText ?? t('frequencyModalAdd')}
      onOpenChange={onOpenChange}
      isSubmitDisabled={
        selectedFrequencies.length === 0 ||
        availableFrequencies.length === 0 ||
        disabled
      }
      onSubmit={() => {
        onAdd(selectedFrequencies);
        onOpenChange(false);
      }}
      contentAutomationId={contentAutomationId}
      submitAutomationId={submitAutomationId}
    >
      {isSingle ? (
        <RadioGroup.Root
          value={selectedFrequency}
          disabled={disabled || availableFrequencies.length === 0}
          onValueChange={(details) => {
            const frequency = availableFrequencies.find(
              (candidate) => candidate === details.value,
            );

            setSelectedFrequencies(frequency ? [frequency] : []);
          }}
        >
          <VStack alignItems="stretch" gap={3} w="full">
            {frequencyItems.map((item) => (
              <RadioGroup.Item key={item.value} value={item.value}>
                <RadioGroup.ItemControl />
                <RadioGroup.ItemHiddenInput />
                <RadioGroup.ItemText>{item.label}</RadioGroup.ItemText>
              </RadioGroup.Item>
            ))}
          </VStack>
        </RadioGroup.Root>
      ) : (
        <VStack alignItems="stretch" gap={3} w="full">
          {frequencyItems.map((item) => {
            const checked = selectedFrequencies.includes(item.value);
            const optionAutomationId =
              frequencyOptionAutomationIds?.[item.value];

            return (
              <Checkbox
                key={item.value}
                checked={checked}
                disabled={disabled || availableFrequencies.length === 0}
                onCheckedChange={(details) => {
                  const isChecked = details.checked === true;

                  setSelectedFrequencies((current) => {
                    if (isChecked) {
                      return current.includes(item.value)
                        ? current
                        : [...current, item.value];
                    }

                    return current.filter(
                      (frequency) => frequency !== item.value,
                    );
                  });
                }}
                {...(optionAutomationId
                  ? automationId(optionAutomationId)
                  : {})}
              >
                {item.label}
              </Checkbox>
            );
          })}
        </VStack>
      )}
    </SettingsEditDialog>
  );
};
